import type { LatLng } from './api.ts';

/**
 * @fileoverview Where the map screen starts, and the links out of it.
 *
 * Pure functions over plain values, so the order of preference is testable
 * without a map, a browser location prompt or a network.
 */

/** Tokyo Station: where the map starts when it knows nothing at all. */
export const FALLBACK_CENTER: LatLng = { lat: 35.681236, lng: 139.767125 };
export const DEFAULT_ZOOM = 15;
/** Zoom used when the map opens on one place: from a link, a search result, or a registration. */
export const FOCUS_ZOOM = 17;
/**
 * How far the map has to move from where it last searched before
 * 「このエリアで探す」 appears. A nudge to read a label is not a new area.
 */
export const RESEARCH_THRESHOLD_METERS = 300;

const LAST_VIEW_KEY = 'mapshare:explore:last-view';

/** A place the URL asks the map to open on: `?place=<id>&lat=&lng=`. */
export interface Focus extends LatLng {
  placeId: string;
}

export function focusFromSearch(search: string): Focus | null {
  const params = new URLSearchParams(search);
  const placeId = params.get('place');
  const lat = Number(params.get('lat'));
  const lng = Number(params.get('lng'));
  if (placeId === null || placeId.length === 0) return null;
  if (params.get('lat') === null || params.get('lng') === null) return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    return null;
  }
  return { placeId, lat, lng };
}

/**
 * A link that opens the map on one place (`?place=…&lat=…&lng=…`). The old
 * registration screen handed places over this way; the URL still works.
 */
export function focusSearch(place: { id: string; lat: number; lng: number }): string {
  return `?${new URLSearchParams({
    place: place.id,
    lat: String(place.lat),
    lng: String(place.lng),
  }).toString()}`;
}

/**
 * Per-browser, per-organization. Storage can be missing or throw (private
 * windows, blocked site data); then there is simply no last view.
 */
export function readLastView(storage: Storage | undefined, slug: string): LatLng | null {
  try {
    const raw = storage?.getItem(`${LAST_VIEW_KEY}:${slug}`);
    if (raw === null || raw === undefined) return null;
    const parsed = JSON.parse(raw) as Partial<LatLng>;
    if (typeof parsed.lat !== 'number' || typeof parsed.lng !== 'number') return null;
    if (!Number.isFinite(parsed.lat) || !Number.isFinite(parsed.lng)) return null;
    return { lat: parsed.lat, lng: parsed.lng };
  } catch {
    return null;
  }
}

export function writeLastView(storage: Storage | undefined, slug: string, view: LatLng): void {
  try {
    storage?.setItem(`${LAST_VIEW_KEY}:${slug}`, JSON.stringify({ lat: view.lat, lng: view.lng }));
  } catch {
    /* a map that forgets where it was is fine */
  }
}

/**
 * "Google マップで開く".
 *
 * With a `place_id` the link lands on that exact place (name, hours, reviews,
 * directions); without one — a place entered by hand — on its coordinates.
 * Google Maps URLs need no key and are free to use.
 */
export function googleMapsUrl(place: {
  name: string;
  lat: number;
  lng: number;
  provider: string;
  providerPlaceId: string | null;
}): string {
  const params = new URLSearchParams({ api: '1' });
  if (place.provider === 'google' && place.providerPlaceId !== null) {
    params.set('query', place.name);
    params.set('query_place_id', place.providerPlaceId);
  } else {
    params.set('query', `${place.lat},${place.lng}`);
  }
  return `https://www.google.com/maps/search/?${params.toString()}`;
}

/** 「350m」「1.2km」 */
export function formatDistance(meters: number): string {
  if (meters < 100) return `${Math.round(meters)}m`;
  if (meters < 1000) return `${Math.round(meters / 10) * 10}m`;
  return `${(meters / 1000).toFixed(1)}km`;
}
